import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, IconCircle } from '@/components/ui';
import { shortDate } from '@/lib/dates';
import { fmtMoney, safeCurrency } from '@/lib/money';
import { displayName, tintIndex } from '@/lib/people';
import { baseAmountOf, expenseShares } from '@core/ledger-core';
import type { Expense, PairwiseBalance } from '@core/ledger-core';

// Ledger pieces shared by the Ledger screen and Today: the balance card
// and an expense row. Money rules are the Ledger's (see the header of
// routes/Ledger.tsx): a row's headline is its NATIVE amount; anything
// derived from the balance is in the household BASE currency.

// The balance line from the viewer's side. A member outside the pair
// (or a non-member viewer) reads it in the third person.
export function heroPhrase(
  pair: PairwiseBalance,
  me: string,
): { text: string; tone: 'owed' | 'owe' | 'neutral' } {
  if (me === pair.to) return { text: `${displayName(pair.from)} owes you`, tone: 'owed' };
  if (me === pair.from) return { text: `You owe ${displayName(pair.to)}`, tone: 'owe' };
  return { text: `${displayName(pair.from)} owes ${displayName(pair.to)}`, tone: 'neutral' };
}

// "Liz owes you" in green or "You owe Jake" in red, with Add expense +
// Settle up for owners. `pair` is the household's first pairwise row
// (two-person households have at most one); null means settled up.
export function BalanceCard({
  pair,
  me,
  baseCurrency,
  isOwner,
  loading,
  error,
  footer,
}: {
  pair: PairwiseBalance | null;
  me: string;
  baseCurrency: string;
  isOwner: boolean;
  loading: boolean;
  error: string | null;
  footer?: ReactNode;
}) {
  const phrase = pair ? heroPhrase(pair, me) : null;
  return (
    <Card className="balance-card">
      {error ? (
        <>
          <p className="balance-label">Couldn't load the balance</p>
          <p className="hint is-owe">{error}</p>
        </>
      ) : loading ? (
        <div aria-busy="true" aria-label="Loading balance">
          <span className="skeleton" style={{ width: 140, height: 20 }} />
          <span className="skeleton" style={{ width: 220, height: 48, marginTop: 10 }} />
          <span className="skeleton" style={{ width: '100%', height: 48, marginTop: 24 }} />
        </div>
      ) : pair && phrase ? (
        <>
          <p className="balance-label">{phrase.text}</p>
          <p
            className={`balance-figure${
              phrase.tone === 'owed' ? ' is-owed' : phrase.tone === 'owe' ? ' is-owe' : ''
            }`}
          >
            {fmtMoney(pair.amountCents, baseCurrency)}
          </p>
          {isOwner && (
            <div className="balance-actions">
              <Button variant="secondary" icon="add" to="/expense/new">
                Add expense
              </Button>
              <Button variant="primary" to="/settle">
                Settle up
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="balance-label">
            <IconCircle icon="check" tone="owed" iconSize={16} />
            All settled up
          </p>
          <p className="balance-figure is-muted">{fmtMoney(0, baseCurrency)}</p>
          {isOwner && (
            <div className="balance-actions">
              <Button variant="primary" icon="add" to="/expense/new">
                Add expense
              </Button>
              <Button variant="secondary" to="/settle">
                Record payment
              </Button>
            </div>
          )}
        </>
      )}
      {footer}
    </Card>
  );
}

// A row's headline figure is its NATIVE amount. Under it: what the
// expense did to the VIEWER, in base currency — computed from the
// canonical expenseShares (base units), never a re-derived split, and
// never subtracted from a native amount.
export function ExpenseRow({
  expense,
  baseCurrency,
  memberEmails,
  me,
}: {
  expense: Expense;
  baseCurrency: string;
  memberEmails: string[];
  me: string;
}) {
  const code = safeCurrency(expense.currency, baseCurrency);
  const base = baseAmountOf(expense);

  let impact: { text: string; cls: string } | null = null;
  if (memberEmails.includes(me)) {
    const myShare = expenseShares(expense, memberEmails)[me] ?? 0;
    if (expense.paidBy === me) {
      const lent = base - myShare;
      if (lent > 0) impact = { text: `you lent ${fmtMoney(lent, baseCurrency)}`, cls: 'is-owed' };
    } else if (myShare > 0) {
      impact = { text: `you borrowed ${fmtMoney(myShare, baseCurrency)}`, cls: 'is-owe' };
    } else {
      impact = { text: 'not involved', cls: '' };
    }
  }

  const vendor = expense.vendor.trim() || '?';
  return (
    <Link to={`/expense/${encodeURIComponent(expense.id)}`} className="row">
      <IconCircle letter={vendor} tint={tintIndex(vendor)} />
      <span className="row-body">
        <span className="row-title">{expense.vendor}</span>
        <span className="row-meta">
          {displayName(expense.paidBy)} paid · {shortDate(expense.date)}
        </span>
      </span>
      <span className="row-end">
        <span className="row-amount">{fmtMoney(expense.amountCents, code)}</span>
        {code !== baseCurrency && (
          <span className="row-sub" title={`Charged in ${baseCurrency}`}>
            {fmtMoney(base, baseCurrency)}
          </span>
        )}
        {impact && <span className={`row-impact ${impact.cls}`}>{impact.text}</span>}
      </span>
    </Link>
  );
}
